import React, { useCallback } from 'react'
import PropTypes from 'prop-types'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import remarkBreaks from 'remark-breaks'
import styled from 'styled-components'
import { Node } from 'vendor/slate'
import { ReactEditor, useReadOnly } from 'vendor/slate-react'
import { sanitizeHeaderAnchors } from 'cg/components/documents/helpers'
import {
  setCopiedDocumentContext,
  writeDocumentContextToClipboard
} from 'cg/components/workbench/shared/documentChatContext'
import { formatBlockRange } from 'cg/components/workbench/shared/buildDocumentChatContext'
import MermaidBlock from 'cg/components/documents/editor/slate/plugins/rawMarkdown/mermaidBlock'
import {
  DocumentParagraph,
  DocumentH1,
  DocumentH2,
  DocumentH3,
  DocumentHR,
  DocumentInlineCode,
  DocumentPre,
  DocumentQuote,
  DocumentQuoteContent,
  DocumentTable,
  DocumentThead,
  DocumentTbody,
  DocumentTr,
  DocumentTh,
  DocumentTd
} from 'cg/components/documents/editor/slate/documentStyles'

const headingText = children =>
  React.Children.toArray(children)
    .map(child => {
      if (typeof child === 'string' || typeof child === 'number') return String(child)
      return child?.props ? headingText(child.props.children) : ''
    })
    .join('')

const MarkdownParagraph = ({ node: _node, ...props }) => <DocumentParagraph {...props} />
const MarkdownH1 = ({ children, node: _node, ...props }) => (
  <DocumentH1 {...props} id={sanitizeHeaderAnchors(headingText(children))}>
    {children}
  </DocumentH1>
)
const MarkdownH2 = ({ children, node: _node, ...props }) => (
  <DocumentH2 {...props} id={sanitizeHeaderAnchors(headingText(children))}>
    {children}
  </DocumentH2>
)
const MarkdownH3 = ({ node: _node, ...props }) => <DocumentH3 {...props} />
const MarkdownHR = ({ node: _node, ...props }) => <DocumentHR {...props} />
const MarkdownBlockquote = ({ children, node: _node, ...props }) => (
  <DocumentQuote {...props}>
    <DocumentQuoteContent>{children}</DocumentQuoteContent>
  </DocumentQuote>
)
const MarkdownCode = ({ inline, className, children, node: _node, ...props }) => {
  if (!inline && /(?:^|\s)language-mermaid(?:\s|$)/.test(className || '')) {
    return <MermaidBlock chart={String(children).replace(/\n$/, '')} />
  }

  if (inline) {
    return (
      <DocumentInlineCode {...props} className={className}>
        {children}
      </DocumentInlineCode>
    )
  }

  return (
    <MarkdownBlockCode {...props} className={className}>
      {children}
    </MarkdownBlockCode>
  )
}
const MarkdownPre = ({ children, node: _node, ...props }) => {
  const child = React.Children.toArray(children)[0]
  if (/(?:^|\s)language-mermaid(?:\s|$)/.test(child?.props?.className || '')) return children
  return <DocumentPre {...props}>{children}</DocumentPre>
}
const MarkdownTable = ({ node: _node, ...props }) => <DocumentTable {...props} />
const MarkdownThead = ({ node: _node, ...props }) => <DocumentThead {...props} />
const MarkdownTbody = ({ node: _node, ...props }) => <DocumentTbody {...props} />
const MarkdownTr = ({ node: _node, ...props }) => <DocumentTr {...props} />
const MarkdownTh = ({ node: _node, ...props }) => <DocumentTh readOnly {...props} />
const MarkdownTd = ({ node: _node, ...props }) => <DocumentTd readOnly {...props} />

const markdownSource = element => Node.string(element)

const locateMarkdownSelection = (markdown, selectionText, sourceHint) => {
  let index = markdown.indexOf(selectionText)
  if (index < 0) return null

  let closest = index
  while (index >= 0) {
    if (Math.abs(index - sourceHint) < Math.abs(closest - sourceHint)) closest = index
    index = markdown.indexOf(selectionText, index + 1)
  }

  return { selectionStart: closest, selectionEnd: closest + selectionText.length }
}

const RawMarkdownWrapper = ({
  attributes,
  children,
  element,
  editor,
  publishedDocument,
  themeColor,
  documentContextEnabled,
  documentId,
  versionId,
  sectionId,
  workspaceId,
  sectionTitle
}) => {
  const slateReadOnly = useReadOnly()
  const renderPreview = publishedDocument || slateReadOnly

  const copySelectionAsDocumentContext = useCallback(
    event => {
      if (!documentContextEnabled) return

      const preview = event.currentTarget
      const domSelection = window.getSelection()
      if (!domSelection || domSelection.rangeCount === 0 || domSelection.isCollapsed) return

      const range = domSelection.getRangeAt(0)
      if (!preview.contains(range.commonAncestorContainer)) return

      const selectionText = domSelection.toString()
      if (!selectionText) return

      const markdown = markdownSource(element)
      let visibleStart = 0
      try {
        const beforeSelection = document.createRange()
        beforeSelection.selectNodeContents(preview)
        beforeSelection.setEnd(range.startContainer, range.startOffset)
        visibleStart = beforeSelection.toString().length
      } catch {
        visibleStart = 0
      }

      const visibleLength = preview.textContent?.length || 0
      const sourceHint = visibleLength ? Math.round((visibleStart / visibleLength) * markdown.length) : 0
      const offsets = locateMarkdownSelection(markdown, selectionText, sourceHint)
      const blockIndex = ReactEditor.findPath(editor, element)[0]
      // If rendered text crosses Markdown syntax and cannot be mapped exactly,
      // keep it a partial selection so it can never become a whole-block patch.
      const isPartialSelection =
        !offsets || offsets.selectionStart > 0 || offsets.selectionEnd < markdown.length

      const context = {
        documentId,
        versionId,
        sectionId,
        workspaceId,
        sectionTitle: sectionTitle || 'Section',
        blockIndex,
        blockIndexEnd: blockIndex,
        blockType: 'raw_markdown',
        blockText: null,
        selectionText,
        selectionStart: offsets?.selectionStart ?? null,
        selectionEnd: offsets?.selectionEnd ?? null,
        isPartialSelection,
        textPreview: selectionText.slice(0, 500),
        isExpanded: true,
        isMultiBlock: false,
        selectionPath: [],
        badgeRange: formatBlockRange(blockIndex, blockIndex, {
          isPartialSelection,
          selectionStart: offsets?.selectionStart,
          selectionEnd: offsets?.selectionEnd
        })
      }

      setCopiedDocumentContext(context)
      writeDocumentContextToClipboard(event.clipboardData, context)
      event.preventDefault()
      event.stopPropagation()
    },
    [
      documentContextEnabled,
      documentId,
      editor,
      element,
      sectionId,
      sectionTitle,
      versionId,
      workspaceId
    ]
  )

  return (
    <RawMarkdownBlock {...attributes}>
      {renderPreview ? (
        <>
          <MarkdownPreview contentEditable={false} $themeColor={themeColor} onCopy={copySelectionAsDocumentContext}>
            <ReactMarkdown
              remarkPlugins={[[remarkGfm, { singleTilde: true }], remarkBreaks]}
              components={{
                p: MarkdownParagraph,
                h1: MarkdownH1,
                h2: MarkdownH2,
                h3: MarkdownH3,
                h4: MarkdownH3,
                h5: MarkdownH3,
                h6: MarkdownH3,
                blockquote: MarkdownBlockquote,
                hr: MarkdownHR,
                code: MarkdownCode,
                pre: MarkdownPre,
                table: MarkdownTable,
                thead: MarkdownThead,
                tbody: MarkdownTbody,
                tr: MarkdownTr,
                th: MarkdownTh,
                td: MarkdownTd
              }}
            >
              {markdownSource(element)}
            </ReactMarkdown>
          </MarkdownPreview>
          <HiddenSlateText>{children}</HiddenSlateText>
        </>
      ) : (
        <MarkdownEditor>{children}</MarkdownEditor>
      )}
    </RawMarkdownBlock>
  )
}

const markdownComponentPropTypes = {
  node: PropTypes.object,
  children: PropTypes.node
}

MarkdownParagraph.propTypes = markdownComponentPropTypes
MarkdownH1.propTypes = markdownComponentPropTypes
MarkdownH2.propTypes = markdownComponentPropTypes
MarkdownH3.propTypes = markdownComponentPropTypes
MarkdownHR.propTypes = markdownComponentPropTypes
MarkdownBlockquote.propTypes = markdownComponentPropTypes

MarkdownCode.propTypes = {
  inline: PropTypes.bool,
  className: PropTypes.string,
  children: PropTypes.node,
  node: PropTypes.object
}

MarkdownPre.propTypes = markdownComponentPropTypes
MarkdownTable.propTypes = markdownComponentPropTypes
MarkdownThead.propTypes = markdownComponentPropTypes
MarkdownTbody.propTypes = markdownComponentPropTypes
MarkdownTr.propTypes = markdownComponentPropTypes
MarkdownTh.propTypes = markdownComponentPropTypes
MarkdownTd.propTypes = markdownComponentPropTypes

RawMarkdownWrapper.propTypes = {
  attributes: PropTypes.object,
  children: PropTypes.node,
  element: PropTypes.object.isRequired,
  editor: PropTypes.object.isRequired,
  publishedDocument: PropTypes.bool,
  themeColor: PropTypes.string,
  documentContextEnabled: PropTypes.bool,
  documentId: PropTypes.string,
  versionId: PropTypes.string,
  sectionId: PropTypes.string,
  workspaceId: PropTypes.string,
  sectionTitle: PropTypes.string
}

export default RawMarkdownWrapper

const RawMarkdownBlock = styled.div`
  width: 100%;
`

const MarkdownEditor = styled.div`
  display: block;
  width: 100%;
  min-height: 1.6em;
  padding: 0;
  margin: 0;
  outline: 0;
  color: inherit;
  background: transparent;
  font: inherit;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
`

const HiddenSlateText = styled.span`
  display: none;
`

const MarkdownPreview = styled.div`
  white-space: normal;

  a {
    color: ${props => props.$themeColor || 'inherit'};
  }

  /* react-markdown wraps list/quote text in <p>; Slate list/quote text does not. */
  li > ${DocumentParagraph} {
    margin-bottom: 0;
  }

  ${DocumentQuoteContent} > ${DocumentParagraph}:last-child {
    margin-bottom: 0;
  }

  /* Preview root resets white-space; restore Slate-equivalent pre behavior. */
  ${DocumentPre} {
    white-space: pre-wrap;
  }
`

const MarkdownBlockCode = styled.code`
  padding: 0;
  color: inherit;
  background: transparent;
`
